import { useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useAuth } from "@/lib/auth-context";
import { searchAccounts, setAccountPassword } from "@/lib/accounts-admin-api";
import { AccountShell } from "@/components/account/AccountShell";
import { ACCOUNTS_ADMIN } from "@/constants/testIds";

const PasswordResetDialog = ({ account, onClose, onDone }) => {
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  if (!account) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      await setAccountPassword(account.id, password);
      toast("Heslo změněno", { description: `Účet ${account.username} má nové heslo.` });
      setPassword("");
      onDone();
    } catch (err) {
      toast("Změna se nezdařila", { description: err.response?.data?.detail ?? "Zkus to prosím znovu." });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open onOpenChange={(next) => !next && onClose()}>
      <DialogContent
        data-testid={ACCOUNTS_ADMIN.dialog}
        className="border-white/10 bg-[#17171b] text-white sm:max-w-md"
      >
        <DialogHeader>
          <DialogTitle className="font-display uppercase tracking-tight">
            Nové heslo - {account.username}
          </DialogTitle>
          <DialogDescription className="text-white/50">
            6-14 znaků, bez mezer a diakritiky. Delší heslo WoW klient neumí na přihlašovací
            obrazovce zadat.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="mb-1.5 block font-display text-[11px] uppercase tracking-[0.16em] text-white/50">
              Nové heslo
            </label>
            <input
              type="text"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              maxLength={14}
              required
              autoComplete="off"
              data-testid={ACCOUNTS_ADMIN.passwordInput}
              className="w-full rounded-md border border-white/10 bg-white/[0.04] px-4 py-2.5 text-white outline-none focus:border-[#F0801F]/60"
            />
            <p className="mt-1 text-right text-[11px] text-white/30">{password.length} / 14</p>
          </div>

          <button
            type="submit"
            disabled={submitting}
            data-testid={ACCOUNTS_ADMIN.submitButton}
            className="w-full rounded-md bg-[#F0801F] py-3 font-display text-sm font-600 uppercase tracking-[0.12em] text-[#141417] transition-colors hover:bg-[#ff9438] disabled:cursor-not-allowed disabled:opacity-60"
          >
            {submitting ? "Ukládám…" : "Změnit heslo"}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export const AccountsAdminPage = () => {
  const { user, loading } = useAuth();
  const [query, setQuery] = useState("");
  const [results, setResults] = useState(null);
  const [resetTarget, setResetTarget] = useState(null);

  const runSearch = async (q) => {
    setQuery(q);
    if (q.trim().length < 2) {
      setResults(null);
      return;
    }
    try {
      setResults(await searchAccounts(q.trim()));
    } catch {
      toast("Vyhledávání se nezdařilo");
    }
  };

  if (loading) {
    return (
      <section className="min-h-[70vh] px-6 pb-24 pt-40 text-center">
        <p className="font-display text-sm uppercase tracking-[0.2em] text-white/40">Načítám…</p>
      </section>
    );
  }

  if (!user) return <Navigate to="/ucet" replace />;

  if (!user.is_gm) {
    return (
      <AccountShell isGm={false} testId={ACCOUNTS_ADMIN.guard}>
        <div className="mx-auto max-w-lg rounded-xl border border-white/10 bg-[#17171b] p-8 text-center">
          <p className="text-sm text-white/60">Správa účtů je jen pro GM.</p>
          <Link to="/ucet" className="mt-4 inline-block text-sm text-[#F0801F] hover:underline">
            Zpět na účet
          </Link>
        </div>
      </AccountShell>
    );
  }

  return (
    <AccountShell isGm={user.is_gm} testId={ACCOUNTS_ADMIN.page}>
      <div className="mb-8">
        <p className="font-display text-xs font-600 uppercase tracking-[0.3em] text-[#F0801F]">
          Nástroje GM
        </p>
        <h1 className="mt-3 font-display text-3xl font-600 uppercase tracking-tight text-white sm:text-4xl">
          Správa účtů
        </h1>
        <p className="mt-3 max-w-xl text-sm text-white/50">
          Vyhledej účet podle uživatelského jména nebo e-mailu a nastav mu nové heslo.
        </p>
      </div>

      <input
        type="text"
        value={query}
        onChange={(e) => runSearch(e.target.value)}
        placeholder="Hledat podle jména nebo e-mailu…"
        data-testid={ACCOUNTS_ADMIN.searchInput}
        className="w-full max-w-md rounded-md border border-white/10 bg-white/[0.04] px-4 py-2.5 text-white outline-none focus:border-[#F0801F]/60"
      />

      <div className="mt-6 space-y-2">
        {query.trim().length >= 2 && results === null && (
          <p className="text-sm text-white/40">Hledám…</p>
        )}
        {results?.length === 0 && <p className="text-sm text-white/50">Žádný účet neodpovídá hledání.</p>}
        {results?.map((acc) => (
          <div
            key={acc.id}
            data-testid={ACCOUNTS_ADMIN.resultRow}
            className="flex flex-wrap items-center justify-between gap-4 rounded-xl border border-white/10 bg-[#17171b] p-5"
          >
            <div>
              <div className="flex items-center gap-2">
                <span className="font-display text-sm font-600 text-white">{acc.username}</span>
                {acc.is_gm && (
                  <span className="rounded-full bg-[#F0801F]/15 px-2 py-0.5 text-[10px] uppercase text-[#F0801F]">
                    GM
                  </span>
                )}
                {acc.online && (
                  <span className="rounded-full bg-emerald-500/15 px-2 py-0.5 text-[10px] uppercase text-emerald-400">
                    Online
                  </span>
                )}
                {acc.locked && (
                  <span className="rounded-full bg-red-500/15 px-2 py-0.5 text-[10px] uppercase text-red-300">
                    Uzamčeno
                  </span>
                )}
              </div>
              <p className="mt-1 text-xs text-white/50">{acc.email}</p>
              <p className="mt-1 text-xs text-white/40">
                Registrace {acc.joindate ? new Date(acc.joindate).toLocaleDateString("cs-CZ") : "?"}
                {acc.last_login && ` · Poslední přihlášení ${new Date(acc.last_login).toLocaleDateString("cs-CZ")}`}
              </p>
            </div>
            <button
              onClick={() => setResetTarget(acc)}
              data-testid={ACCOUNTS_ADMIN.resetButton}
              className="rounded-md border border-white/20 px-3 py-1.5 text-xs uppercase tracking-wide text-white/70 hover:border-[#F0801F]/60 hover:text-[#F0801F]"
            >
              Změnit heslo
            </button>
          </div>
        ))}
      </div>

      <PasswordResetDialog account={resetTarget} onClose={() => setResetTarget(null)} onDone={() => setResetTarget(null)} />
    </AccountShell>
  );
};
